import { signal } from "@preact/signals";
import { useRef, useLayoutEffect } from "preact/hooks";
import {
  formatMoney,
  lineTotalCents,
  paidQtyOf,
  hasOptions,
  requiresNote,
  isWithinSchedule,
  scheduleLabel,
  type MenuItem,
  type MenuCategoryId,
  type OrderLineId,
  type OrderLine,
  type SizeVariant,
} from "@orderman/shared";
import {
  menu,
  draft,
  activeCategory,
  draftTotalCents,
  waitStats,
  addItem,
  changeQty,
  updateLineNote,
  updateLineOptions,
  openPayment,
  changeTable,
  tablesConfig,
  tableAllowed,
  now,
  remainingOf,
  isOrderable,
  LOW_STOCK,
} from "../state/store.js";
import { payableOf } from "../state/draft.js";
import { ModifierSheet } from "./ModifierSheet.js";
import { NoteSheet } from "./NoteSheet.js";

// Artikel, für den gerade der Options-Dialog offen ist (null = keiner).
const modifierTarget = signal<MenuItem | null>(null);
// Info-/Notiz-Pseudo-Artikel, für den eine Notiz erfasst wird (Pflicht-Text).
const noteForItem = signal<MenuItem | null>(null);
// Nachträgliches Bearbeiten der Notiz einer ungesendeten Bonzeile.
const noteForLine = signal<{ id: OrderLineId; name: string; initial: string } | null>(null);
// Nachträgliches Bearbeiten von Größe/Beilage/Extras/Notiz einer ungesendeten Bonzeile
// (Artikel mit Optionen — sonst reicht das reine Notiz-Sheet oben).
const lineEditTarget = signal<{ item: MenuItem; line: OrderLine } | null>(null);

/** Aus dem gespeicherten Zeilennamen ("<Basis> <Größe>") die ursprünglich gewählte
 *  Variante zurückgewinnen — die Zeile speichert nur Name+Preis, keine Variant-Referenz. */
function resolveInitialVariant(item: MenuItem, line: OrderLine): SizeVariant | null {
  const variants = item.variants ?? [];
  if (variants.length === 0) return null;
  const base = item.shortLabel || item.name;
  return variants.find((v) => line.name === `${base} ${v.label}`) ?? variants[0] ?? null;
}

/** ✏ an einer Bonzeile: bei Artikeln mit Optionen den vollen Editor öffnen, sonst nur die Notiz. */
function openLineEditor(line: OrderLine): void {
  const item = menu.value?.items.find((i) => i.id === line.menuItemId);
  if (item && hasOptions(item)) {
    lineEditTarget.value = { item, line };
  } else {
    openLineNoteEditor(line);
  }
}
// Tisch-Korrektur-Tastatur (null = geschlossen, sonst aktueller Ziffern-Puffer).
const tableEdit = signal<string | null>(null);
const tableEditError = signal("");

const TKEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"];

function commitTable(label: string): void {
  if (!tableAllowed(label)) {
    tableEditError.value = `${label} ist nicht freigegeben.`;
    return;
  }
  changeTable(label);
  tableEdit.value = null;
  tableEditError.value = "";
}

function TableEditModal() {
  const press = (k: string) => {
    tableEditError.value = "";
    const cur = tableEdit.value ?? "";
    if (k === "⌫") tableEdit.value = cur.slice(0, -1);
    else if (k === "OK") {
      if (cur) commitTable(`Tisch ${cur}`);
    } else if (cur.length < 4) tableEdit.value = cur + k;
  };
  const extras = tablesConfig.value.extras;
  return (
    <div class="modal-backdrop" onClick={() => (tableEdit.value = null)}>
      <div class="sheet" onClick={(e) => e.stopPropagation()}>
        <div class="sheet-head">
          <span class="sheet-title">Tisch korrigieren</span>
          <button class="icon-btn dark" onClick={() => (tableEdit.value = null)}>
            ✕
          </button>
        </div>
        <div class="tablepad" style="padding:16px;margin:0;width:auto">
          <div class="tablepad-display">{tableEdit.value || "—"}</div>
          {tableEditError.value && <div class="tablepad-error">{tableEditError.value}</div>}
          <div class="keypad">
            {TKEYS.map((k) => (
              <button
                key={k}
                class={`key ${k === "OK" ? "key-ok" : ""} ${k === "⌫" ? "key-del" : ""}`}
                onClick={() => press(k)}
              >
                {k}
              </button>
            ))}
          </div>
          {extras.length > 0 && (
            <div class="tablepad-extras">
              {extras.map((e) => (
                <button key={e} class="extra-btn" onClick={() => commitTable(e)}>
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function onItemClick(item: MenuItem): void {
  // Info-Pseudo-Artikel: direkt das Notiz-Sheet (Pflicht-Text) — kein Modifier-Schritt.
  if (requiresNote(item)) noteForItem.value = item;
  else if (hasOptions(item)) modifierTarget.value = item;
  else addItem(item);
}

// Blättern zwischen den Kategorien als echtes Carousel: prev/current/next liegen als drei
// Panels nebeneinander in einer Spur (.grid-track). Beim Ziehen verschiebt sich die Spur mit
// dem Finger, sodass der Nachbar-Tab schon sichtbar hereinkommt; beim Loslassen wird die
// Bewegung fortgeführt (Umschalten) oder federt zurück. Die Drag-Mechanik steckt in
// OrderingView (Refs + useLayoutEffect); der Transform wird imperativ gesetzt (kein Re-Render
// pro Move). Vertikales Scrollen bleibt nativ (Achsen-Lock + touch-action: pan-y im CSS).
const SWIPE_COMMIT = 70; // ab dieser horizontalen Distanz (px) wird umgeschaltet

function sortedCats() {
  const m = menu.value;
  return m ? [...m.categories].sort((a, b) => a.sortOrder - b.sortOrder) : [];
}

function neighborCategory(delta: number): MenuCategoryId | null {
  const cats = sortedCats();
  const idx = cats.findIndex((c) => c.id === activeCategory.value);
  if (idx < 0) return null;
  return cats[idx + delta]?.id ?? null;
}

function itemsOfCategory(catId: MenuCategoryId | null): MenuItem[] {
  const m = menu.value;
  if (!m || !catId) return [];
  return m.items.filter((i) => i.categoryId === catId).sort((a, b) => a.sortOrder - b.sortOrder);
}

// Ein Kategorie-Panel (scrollbares Kachelraster) — als eigene Komponente, damit prev/current/
// next unabhängig gerendert in der Spur nebeneinanderliegen.
function CategoryPanel({ catId }: { catId: MenuCategoryId | null }) {
  return (
    <div class="grid">
      {itemsOfCategory(catId).map((it) => {
        const rem = remainingOf(it);
        const inSchedule = isWithinSchedule(it, new Date(now.value));
        const status = !it.isAvailable
          ? "deaktiviert"
          : !inSchedule
            ? scheduleLabel(it) || "z. Zt. nicht"
            : rem != null && rem <= 0
              ? "ausverkauft"
              : rem != null && rem <= LOW_STOCK
                ? `nur noch ${rem}`
                : "";
        const low = inSchedule && rem != null && rem > 0 && rem <= LOW_STOCK;
        return (
          <button
            key={it.id}
            class="item"
            disabled={!isOrderable(it)}
            style={it.color ? { background: it.color } : undefined}
            onClick={() => onItemClick(it)}
          >
            <span class="item-name">
              {it.shortLabel || it.name}
              {hasOptions(it) && <span class="opt-badge">▾</span>}
            </span>
            {(waitStats.value[it.id]?.avgMs ?? 0) >= 120000 && (
              <span class="item-wait">~{Math.round(waitStats.value[it.id]!.avgMs / 60000)}′</span>
            )}
            {status && <span class={`item-status ${low ? "low" : ""}`}>{status}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function OrderingView() {
  const cats = sortedCats();
  const idx = cats.findIndex((c) => c.id === activeCategory.value);
  const prevCat = idx > 0 ? cats[idx - 1]!.id : null;
  const curCat = idx >= 0 ? cats[idx]!.id : (cats[0]?.id ?? null);
  const nextCat = idx >= 0 && idx < cats.length - 1 ? cats[idx + 1]!.id : null;

  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ x: 0, y: 0, axis: "" as "" | "h" | "v", dx: 0, active: false, w: 0, swiped: false });
  const commitTimer = useRef(0);

  // Nach jedem Kategoriewechsel (Swipe-Commit ODER Tab-Klick) die Spur ohne Sprung auf das
  // mittlere Panel zentrieren — läuft vor dem Paint, daher nahtlos.
  useLayoutEffect(() => {
    const t = trackRef.current, v = viewportRef.current;
    if (!t || !v) return;
    t.style.transition = "none";
    t.style.transform = `translateX(${-v.clientWidth}px)`;
  }, [activeCategory.value]);

  function setTrack(x: number, animate: boolean): void {
    const t = trackRef.current;
    if (!t) return;
    if (animate) {
      // Reflow erzwingen, damit der Browser die aktuelle (transition:none) Position als
      // Animations-Start festschreibt — sonst springt der Transform ohne zu animieren.
      void t.offsetWidth;
      t.style.transition = "transform .2s ease-out";
    } else {
      t.style.transition = "none";
    }
    t.style.transform = `translateX(${x}px)`;
  }

  function onPointerDown(e: PointerEvent): void {
    if (!e.isPrimary || (e.pointerType === "mouse" && e.button !== 0)) return;
    const v = viewportRef.current;
    if (!v) return;
    clearTimeout(commitTimer.current); // ein noch ausstehendes Umschalten abbrechen
    drag.current = { x: e.clientX, y: e.clientY, axis: "", dx: 0, active: true, w: v.clientWidth, swiped: false };
    setTrack(-v.clientWidth, false); // laufende Animation stoppen, zentriert bleiben
  }

  function onPointerMove(e: PointerEvent): void {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (d.axis === "") {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return; // Geste noch zu klein zum Festlegen
      d.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? "h" : "v";
      if (d.axis === "h") {
        d.swiped = true; // ab jetzt ist es ein Wisch → folgenden Klick schlucken
        try { viewportRef.current?.setPointerCapture(e.pointerId); } catch { /* egal */ }
      }
    }
    if (d.axis !== "h") return; // dominant vertikal → natives Scrollen im Panel
    // Am Rand (keine Nachbar-Kategorie in Richtung) gummiartiger Widerstand statt freiem Zug.
    const hasNeighbor = neighborCategory(dx > 0 ? -1 : 1) !== null;
    d.dx = hasNeighbor ? dx : dx * 0.25;
    setTrack(-d.w + d.dx, false);
  }

  function onPointerEnd(): void {
    const d = drag.current;
    if (d.axis === "h") {
      const delta = d.dx > 0 ? -1 : 1; // rechts wischen → vorige, links → nächste Kategorie
      const target = neighborCategory(delta);
      if (Math.abs(d.dx) >= SWIPE_COMMIT && target) {
        // Bewegung fortführen: das Nachbar-Panel komplett in die Mitte schieben, dann umschalten.
        // Panels liegen als [prev, cur, next]; prev zentrieren = translateX 0, next = -2w.
        // Umschalten per Timer (nicht transitionend — das feuert nicht zuverlässig, wenn die
        // Animation ausbleibt/unterbrochen wird). useLayoutEffect zentriert danach ohne Sprung.
        setTrack(delta < 0 ? 0 : -2 * d.w, true);
        clearTimeout(commitTimer.current);
        commitTimer.current = window.setTimeout(() => { activeCategory.value = target; }, 210);
      } else {
        setTrack(-d.w, true); // Schwelle nicht erreicht → zurückfedern
      }
    }
    d.active = false;
    d.axis = "";
    d.dx = 0;
  }

  // Nach einem Wisch den synthetischen Klick abfangen, damit nicht die Kachel darunter feuert.
  function onClickCapture(e: MouseEvent): void {
    if (drag.current.swiped) {
      e.stopPropagation();
      e.preventDefault();
      drag.current.swiped = false;
    }
  }

  return (
    <div class="ordering">
      <div class="cats">
        {cats.map((c) => (
          <button
            key={c.id}
            class={`cat ${activeCategory.value === c.id ? "active" : ""}`}
            style={c.color ? { borderColor: c.color } : undefined}
            onClick={() => (activeCategory.value = c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div
        class="grid-viewport"
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={onClickCapture}
      >
        <div class="grid-track" ref={trackRef}>
          <CategoryPanel catId={prevCat} />
          <CategoryPanel catId={curCat} />
          <CategoryPanel catId={nextCat} />
        </div>
      </div>

      <OrderPanel />

      {tableEdit.value !== null && <TableEditModal />}

      {modifierTarget.value && (
        <ModifierSheet
          item={modifierTarget.value}
          onClose={() => (modifierTarget.value = null)}
          onConfirm={({ variant, mods, note }) => {
            addItem(modifierTarget.value!, mods, variant, note);
            modifierTarget.value = null;
          }}
        />
      )}

      {noteForItem.value && (
        <NoteSheet
          title={noteForItem.value.shortLabel || noteForItem.value.name}
          required
          onClose={() => (noteForItem.value = null)}
          onConfirm={(text) => {
            addItem(noteForItem.value!, [], null, text);
            noteForItem.value = null;
          }}
        />
      )}

      {noteForLine.value && (
        <NoteSheet
          title={`Notiz: ${noteForLine.value.name}`}
          initialText={noteForLine.value.initial}
          confirmLabel="Übernehmen"
          onClose={() => (noteForLine.value = null)}
          onConfirm={(text) => {
            updateLineNote(noteForLine.value!.id, text);
            noteForLine.value = null;
          }}
        />
      )}

      {lineEditTarget.value && (
        <ModifierSheet
          item={lineEditTarget.value.item}
          initial={{
            variant: resolveInitialVariant(lineEditTarget.value.item, lineEditTarget.value.line),
            mods: lineEditTarget.value.line.modifiers,
            note: lineEditTarget.value.line.note ?? "",
          }}
          confirmLabel="Übernehmen"
          onClose={() => (lineEditTarget.value = null)}
          onConfirm={({ variant, mods, note }) => {
            updateLineOptions(lineEditTarget.value!.line.id, lineEditTarget.value!.item, variant, mods, note);
            lineEditTarget.value = null;
          }}
        />
      )}
    </div>
  );
}

/** Vom Bonpanel aus: ✏ an einer noch nicht gesendeten Zeile öffnet das Notiz-Sheet. */
export function openLineNoteEditor(line: { id: OrderLineId; name: string; note?: string }): void {
  noteForLine.value = { id: line.id, name: line.name, initial: line.note ?? "" };
}

function OrderPanel() {
  const d = draft.value!;
  const pay = payableOf(d);
  const m = menu.value;
  return (
    <div class="panel">
      <div class="lines">
        {d.lines.length === 0 ? (
          <div class="panel-empty">Artikel antippen, um sie hinzuzufügen</div>
        ) : (
          d.lines.map((dl) => {
            const paid = paidQtyOf(pay, dl.line.id);
            const fullyPaid = paid >= dl.line.qty;
            const canReduce = dl.line.qty > paid;
            const voided = dl.line.status === "voided";
            const isInfo = m?.items.find((i) => i.id === dl.line.menuItemId)?.isInfo === true;
            // ✏ nur an noch editierbaren Zeilen (im Entwurf, nicht gesendet/bezahlt/storniert).
            const canEdit = !dl.sent && !voided && !fullyPaid;
            return (
              <div key={dl.line.id} class={`line ${voided ? "voided" : ""} ${fullyPaid ? "paidline" : ""} ${isInfo ? "infoline" : ""}`}>
                <span class="qty">{isInfo ? "💬" : `${dl.line.qty}×`}</span>
                <span class="lname">
                  {dl.line.name}
                  {dl.line.modifiers.length > 0 && (
                    <em class="lmods">{dl.line.modifiers.map((mm) => mm.name).join(", ")}</em>
                  )}
                  {dl.line.note && <em class="lnote">„{dl.line.note}"</em>}
                  {paid > 0 && <em class="paid-tag">✓ {fullyPaid ? "bezahlt" : `${paid} bez.`}</em>}
                  {dl.sent && paid === 0 && <em class="sent-tag">✓ gesendet</em>}
                </span>
                {!isInfo && <span class="lprice">{formatMoney(lineTotalCents(dl.line))}</span>}
                {/* Gesendete Zeilen sind gesperrt (stehen an der Station schon fest). */}
                {!voided && !fullyPaid && !dl.sent && (
                  <span class="lbtns">
                    {canEdit && (
                      <button
                        onClick={() => openLineEditor(dl.line)}
                        aria-label="Bearbeiten"
                        title="Bearbeiten"
                      >
                        ✎
                      </button>
                    )}
                    {!isInfo && (
                      <>
                        <button onClick={() => changeQty(dl.line.id, 1)} aria-label="mehr">
                          ＋
                        </button>
                        <button disabled={!canReduce} onClick={() => changeQty(dl.line.id, -1)} aria-label="weniger">
                          －
                        </button>
                      </>
                    )}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
      <div class="panel-foot">
        <span class="total">{formatMoney(draftTotalCents.value)}</span>
        <button
          class="table-chip"
          onClick={() => (tableEdit.value = d.tableRef.replace(/\D/g, ""))}
          title="Tischnummer korrigieren"
        >
          {d.tableRef} ✎
        </button>
        <button
          class="pay"
          disabled={!d.lines.some((dl) => dl.line.status !== "voided")}
          onClick={() => void openPayment()}
        >
          Kassieren
        </button>
      </div>
    </div>
  );
}
